import { useEffect, useState } from 'react';
import { alert, haptic } from '../../lib/telegram';
import { useT } from '../../lib/i18n';
import { api } from './api';

/**
 * Who pays for this client.
 *
 * A husband buying a package for himself and his wife is one wallet and
 * two players. Two accounts so each can be rated, messaged and given
 * their own notes; one balance, because that is where the money is.
 *
 * Bookings then draw on the payer's credits without either of them
 * having to think about it.
 */
export function PayerSection({ clientId, name, currentPayer, onChanged }: {
  clientId: string; name: string; currentPayer: string | null; onChanged: () => void;
}) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const [clients, setClients] = useState<any[] | null>(null);
  const [search, setSearch] = useState('');
  const [payerName, setPayerName] = useState<string | null>(null);

  useEffect(() => {
    if (!currentPayer) { setPayerName(null); return; }
    api<{ clients: any[] }>('/api/coach/insights')
      .then((r) => setPayerName(r.clients.find((c) => c.id === currentPayer)?.name ?? 'someone'))
      .catch(() => setPayerName('someone'));
  }, [currentPayer]);

  async function open() {
    setPicking(true);
    if (clients === null) {
      try {
        const r = await api<{ clients: any[] }>('/api/coach/insights');
        setClients(r.clients.filter((c) => c.id !== clientId));
      } catch { setClients([]); }
    }
  }

  async function set(payerId: string | null) {
    try {
      await api(`/api/coach/clients/${clientId}/payer`, {
        method: 'PUT', body: JSON.stringify({ payer_id: payerId }),
      });
      haptic.success();
      setPicking(false);
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'payer_has_a_payer'
        ? t('co.cd.payerTaken')
        : t('co.cd.couldNotDo', { message: err.message }));
    }
  }

  const shown = (clients ?? [])
    .filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 10);

  return (
    <>
      <div className="section-title">{t('co.cd.payingTitle')}</div>
      <div className="list">
        <div className="card">
          {currentPayer ? (
            <>
              <div className="card__title">{t('co.cd.paysFor', { payer: payerName ?? '…', name })}</div>
              <div className="card__when" style={{ marginTop: 4 }}>
                {t('co.cd.paysForBody', { payer: payerName ?? '…' })}
              </div>
              <div className="card__foot">
                <span />
                <button className="card__cancel" onClick={() => set(null)}>
                  {t('co.cd.paysSelf', { name })}
                </button>
              </div>
            </>
          ) : picking ? (
            <>
              <input className="paybar__opt" style={{ width: '100%', textAlign: 'left' }}
                placeholder={t('co.cd.search')} value={search} autoFocus
                onChange={(e) => setSearch(e.target.value)} />
              <div style={{ maxHeight: '34vh', overflowY: 'auto', marginTop: 8 }}>
                {clients === null ? <div className="skeleton" /> :
                 shown.map((c) => (
                  <button key={c.id} className="daysheet__row"
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, borderBottom: '1px solid var(--line-soft)' }}
                    onClick={() => set(c.id)}>
                    <span style={{ flex: 1 }}>{c.name}</span>
                    <span className="card__when">{c.credits > 0 ? t('co.cd.credits', { n: c.credits }) : ''}</span>
                  </button>
                ))}
              </div>
              <button className="paybar__opt" style={{ width: '100%', marginTop: 8 }}
                onClick={() => setPicking(false)}>{t('co.cd.cancel')}</button>
            </>
          ) : (
            <button className="paybar__opt" style={{ width: '100%' }} onClick={open}>
              {t('co.cd.someoneElsePays')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
